// src/lib/homeSnapshot.ts
import type { HomeDish } from './homeDishes';

export type HomeSnapshot = {
  key: string;
  ts: number;
  list: HomeDish[];
};

const DB_NAME = 'bw-home';
const DB_VERSION = 2;
const STORE = 'snapshots';

let openPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (openPromise) return openPromise;
  openPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return openPromise;
}

export function makeHomeSnapshotKey(params: {
  uid?: string | null;
  pincode?: string | null;
  city?: string | null;
  addressLabel?: string | null;
}): string {
  const { uid, pincode, city, addressLabel } = params;
  const scope = [pincode, city, addressLabel].filter(Boolean).join('|') || 'default';
  return `v3::${uid || 'anon'}::${scope}`;
}

export async function getHomeSnapshot(key: string): Promise<HomeSnapshot | null> {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE, 'readonly');
      const store = tx.objectStore(STORE);
      const req = store.get(key);
      req.onsuccess = () => resolve((req.result as HomeSnapshot) || null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function setHomeSnapshot(key: string, list: HomeDish[]): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const entry: HomeSnapshot = { key, ts: Date.now(), list };
      store.put(entry);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    /* ignore */
  }
}

export async function clearHomeSnapshots(): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      store.clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    /* ignore */
  }
}
